import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  setInjectedStyles,
  setInjectedPosition,
  setlauncherContent,
  setActiveTriggerMessage,
  setShowLuancher,
  toggleLauncher,
  setAnimateWidget,
} from 'app/redux/widget/widget.actions'
import { appLocalStorageObject } from 'app/redux/messages/messages.adapter'
import { triggerEvent } from 'app/utils/event'
import { getUserTokenKey, removeAllUserDataFromStorage } from 'app/utils/browser'
import {
  getUserFromApi,
  setCurrentUser,
  setActiveDepartment,
  setNewMessage,
  restartFlowOperation,
  dispatchFormAndMessage,
} from 'app/redux/messages/messages.actions'
import { LAUNCHER_CONTENT_STATE } from 'app/constants/launcherContentStates'
import { ON_NEW_USER_LOADED, ON_USER_DETAILS_UPDATED } from 'app/constants/events'
import { ON_WIDGET_READY } from 'app/constants/socketEvents'
import { UPDATE_USER_DATA_URL } from 'app/constants/socketUrls'
// import * as Sentry from '@sentry/react'

const getUser = ({ messagesData }) => messagesData.user
const getDefaultTeam = ({ messagesData }) => messagesData.defaultTeam
const getShowLauncher = ({ widgetData }) => widgetData.widgetInfo.showLauncher
const getIsWidgetReady = ({ widgetData }) => widgetData.widgetInfo.isWidgetReady

// hook for initializing all widget's API that are provided in https://raychat.io/documentation
// NOTE that for `close` and `open` events, a custom event named `triggerWidgetOpenCloseEvent` is defined and will be dispatched wherever user manually changes widget status
function useWidgetApi() {
  const user = useSelector(getUser)
  const defaultTeam = useSelector(getDefaultTeam)
  const showLauncher = useSelector(getShowLauncher)
  const isWidgetReady = useSelector(getIsWidgetReady)

  const Raychat = {}

  const dispatch = useDispatch()

  const setCustomStyle = (styles) => {
    const styleObj = {}

    if (styles.backgroundColor) styleObj['backgroundColor'] = styles.backgroundColor
    if (styles.borderRadius) styleObj['borderRadius'] = styles.borderRadius
    if (styles.width) styleObj['width'] = styles.width
    if (styles.height) styleObj['height'] = styles.height
    if (styles.boxShadow) styleObj['boxShadow'] = styles.boxShadow

    dispatch(setInjectedStyles(styleObj))
  }

  const setCustomPosition = (position) => {
    const positionObj = {
      topSpace: 'auto',
      rightSpace: 'auto',
      bottomSpace: 'auto',
      leftSpace: 'auto',
    }

    if (position.right) positionObj['rightSpace'] = position.right
    if (position.left) positionObj['leftSpace'] = position.left
    if (position.top) positionObj['topSpace'] = position.top
    if (position.bottom) positionObj['bottomSpace'] = position.bottom

    dispatch(setInjectedPosition(positionObj))
  }

  const setUserData = async (user) => {
    const {
      id,
      token,
      email,
      name,
      phone,
      other,
      about,
      fullName,
      fullname,
      avatar,
      shownField,
      updatedOnce,
      metaData,
    } = user || {}

    try {
      await window.socket.emit(
        'http',
        {
          method: 'post',
          url: UPDATE_USER_DATA_URL,
          data: {
            id,
            token,
            email,
            name,
            phone,
            other,
            about,
            fullName,
            fullname,
            avatar,
            shownField,
            updatedOnce,
            metaData,
          },
        },
        (response) => {
          if (response.success) {
            dispatch(setCurrentUser(user))
            dispatch(getUserFromApi(true))
            return user
          }
        },
      )
    } catch (error) {
      console.log(error)
      // Sentry.captureMessage(error, 'error')
    }
  }

  const setCustomUser = async (inputUser) => {
    inputUser.other = true
    inputUser.id = appLocalStorageObject.getItem(getUserTokenKey())
    await setUserData(inputUser)
    appLocalStorageObject.setItem('clientName', inputUser.name)
  }

  const getUserDetails = () => user

  // Substitutes the current user whith a user with the given userId
  const loadUser = (userId) => {
    removeAllUserDataFromStorage()
    const tokenKey = getUserTokenKey()
    localStorage.setItem(tokenKey, userId)
    triggerEvent(ON_NEW_USER_LOADED)

    dispatch(restartFlowOperation())
  }

  const unloadUser = () => removeAllUserDataFromStorage()

  const sendOfflineMessage = (message) => {
    dispatch(setActiveDepartment(defaultTeam._id))
    dispatch(setlauncherContent(LAUNCHER_CONTENT_STATE.FIRST_CHAT))

    dispatch(
      setNewMessage({
        message: {
          me: false,
          avatar: '',
          type: 'text',
          content: message,
          isAutoMessage: true,
        },
        teamId: defaultTeam._id,
      }),
    )
    dispatch(
      setActiveTriggerMessage({
        message: message,
        agent_id: null,
        avatar: null,
        title: 'raychat',
        isAutoMessage: true,
      }),
    )
  }

  const openWidget = () => dispatch(setShowLuancher(true))

  const closeWidget = () => dispatch(setShowLuancher(false))

  const toggleWidget = () => dispatch(toggleLauncher())

  const animateWidget = (animationType) => {
    dispatch(
      setAnimateWidget({
        status: true,
        animationType: animationType,
      }),
    )
  }

  /**
   * @callback successCb
   * @param object[] - submitted values
   */

  /**
   * @param {
   * inputs: {label: string,type: string}[]
   * message: string
   * title: string,
   * formName: string,
   * notSendToServer: boolean,
   * disableChatbox: boolean
   * } fields
   */
  const sendForm = (formObject) => {
    dispatch(
      dispatchFormAndMessage({
        message: `<p>${formObject.message}</p>`,
        formTitle: formObject.title || '',
        formFields: formObject.inputs,
        formId: new Date().getTime(),
        shouldDisableInput: !!formObject.disableChatbox,
        formName: formObject.formName || 'api',
        notSendToServer: !!formObject.notSendToServer,
        teamId: 'all',
      }),
    )
  }

  /**
   * this functions assigns event listeners into Raychat object
   * @param {object} rayObj
   * @param {object} windowObj
   */
  const assignEventListeners = (rayObj, windowObj) => {
    rayObj.on = (eventType, listener) => {
      windowObj.addEventListener(eventType, listener)
    }
    windowObj.Raychat = rayObj
  }

  const isWidgetOpen = () => showLauncher

  useEffect(() => {
    triggerEvent(ON_USER_DETAILS_UPDATED, user)
  }, [user])

  useEffect(() => {
    if (isWidgetReady) {
      // the reason why assignment is happening seperately in both parent and window object is because if we pass the same object it causes firing event twice
      assignEventListeners(Raychat, window)
      const parentRaychatObj = { ...Raychat }

      if (window.parent) {
        assignEventListeners(parentRaychatObj, window.parent)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLauncher, user])

  useEffect(() => {
    if (isWidgetReady) {
      triggerEvent(ON_WIDGET_READY, '')
    }
  }, [isWidgetReady])

  // subscribing function
  Raychat.setStyle = setCustomStyle
  Raychat.setPosition = setCustomPosition
  Raychat.setUser = setCustomUser
  Raychat.getUser = getUserDetails
  Raychat.loadUser = loadUser
  Raychat.unloadUser = unloadUser
  Raychat.sendOfflineMessage = sendOfflineMessage
  Raychat.openWidget = openWidget
  Raychat.closeWidget = closeWidget
  Raychat.toggle = toggleWidget
  Raychat.animate = animateWidget
  Raychat.isWidgetOpen = isWidgetOpen
  Raychat.sendForm = sendForm
}

export default useWidgetApi
